import React, { useCallback, useMemo } from 'react';

import AnomalyLocation from '../anomaly-location';
import Conditional from '../../../common/conditional';
import CustomSection from '../../custom-general/custom-section';
import CustomGeneralPhotos from '../../custom-general/custom-general-photos';
import Checklist from '../checklist';
import CustomUTMAnomaly from '../../custom-utm/customUTMAnomaly';
import GeneralDrawing from '../general-drawing';
import PDOSummaryReport from '../pdo-summary';
import PID from '../pid';
import RiskRating from '../../../pages/reports/riskRating';

import { getSplitData, matchPhrases } from '../../../../utils/utilities';
import { AnomalyLocationData, ChecklistItem, FVIStructureItem, PDFImageProps, PIDData } from '../../../../utils/models';

import useProjectDetails from '../../../../hooks/useProjectDetails';
import config from '../../../../utils/config';

interface TankAnalyticsProps {
	additionalComment: string;
	anomalyLocationData: AnomalyLocationData;
	checklistItems: ChecklistItem[];
	items: FVIStructureItem[];
	pidData: PIDData;
}

const TankAnalytics: React.FC<TankAnalyticsProps> = ({ additionalComment, anomalyLocationData, checklistItems, items, pidData }) => {
	const { projectDetails } = useProjectDetails();

	const getAnomalyHeaders = useCallback((item: FVIStructureItem) => {
		return [
			{
				label: 'Tank Section',
				value: item.structureMemberName,
			},
		];
	}, []);

	const checklistHeaders = useMemo(() => {
		return [
			{
				label: 'Tank',
				value: projectDetails ? projectDetails.equipmentTag : '',
			},
		];
	}, [projectDetails]);

	const pdoItems = useMemo(() => {
		return items.map((item) => ({ ...item, hasPID: pidData.hasPID, pIDNumber: pidData.pIDNumber }));
	}, [items, pidData]);

	return (
		<>
			<PDOSummaryReport 
				includeEmptyReportNotice={false}
				itemKey='structureMemberName' 
				itemLabel='Structure Member Name' 
				items={pdoItems} 
			/>

			{getSplitData<PDFImageProps>(pidData.pIDImages, 1)
				.map((pid, index: number) => (
					<PID
						hasPID={pidData.hasPID}
						key={index} 
						pidImages={pid} 
						pidNumber={pidData.pIDNumber} 
						sectionTitle="Inspection Findings" 
						sectionSubtitle="P&ID" 
					/>
				))}

			<Checklist
				enableChecklistItemComment
				headers={checklistHeaders}
				checklistItems={checklistItems}
				additionalComment={additionalComment}
			/>

			{items.map((item) =>(	
				<React.Fragment key={item.structureMemberName}>
					<CustomUTMAnomaly
						identifier={item.structureMemberName}
						lineNumberID={item.id}
						anomalyItems={item.anomalyItems}
						headers={getAnomalyHeaders(item)}
					/>
				
					<Conditional isVisible={matchPhrases(item.hasAnomalies, 'no')}>
						<GeneralDrawing generalDrawing={item.generalDrawing} />
					</Conditional>

					<Conditional isVisible={matchPhrases(item.hasAnomalies, 'no') && item.generalPhotos.length > 0}>
						<CustomGeneralPhotos identifier={item.structureMemberName} label="Inspection Findings" subLabel="Photographic Record" photos={item.generalPhotos} />
					</Conditional>
				</React.Fragment>
			))}

			<Conditional isVisible={matchPhrases(anomalyLocationData.hasAnomalyLocations, 'yes') && anomalyLocationData.anomalyLocations.length > 0}>
				<AnomalyLocation locationImages={anomalyLocationData.anomalyLocations} />
			</Conditional>

			<Conditional isVisible={config.enableRiskRating}>
				<CustomSection sectionTitle="Risk Rating">
					<Conditional isVisible={items.length > 0}>
						<RiskRating data={items} />
					</Conditional>
				</CustomSection>
			</Conditional>
		</>
	);
};

export default TankAnalytics;
